import type { ScanStatus, ScanView } from "../../api/types.js";

export const SCAN_DIAGRAM_NODE_IDS = [
  "authorized-source",
  "immutable-snapshot",
  "detectors",
  "independent-replay",
  "index",
  "findings",
] as const;

export type ScanDiagramNodeId = (typeof SCAN_DIAGRAM_NODE_IDS)[number];

export type ScanDiagramOutcome = "in-progress" | "complete" | "warning" | "failed" | "canceled";

export type ScanDiagramNodeTone = ScanDiagramOutcome | "locked" | "pending";

export interface ScanDiagramNode {
  id: ScanDiagramNodeId;
  label: string;
  tone: ScanDiagramNodeTone;
  locked: boolean;
}

export interface ScanDiagram {
  outcome: ScanDiagramOutcome;
  gateLocked: boolean;
  nodes: readonly ScanDiagramNode[];
}

const NODE_LABELS: Record<ScanDiagramNodeId, string> = {
  "authorized-source": "Authorized source",
  "immutable-snapshot": "Immutable snapshot",
  detectors: "Detectors",
  "independent-replay": "Independent replay",
  index: "Index",
  findings: "Findings",
};

const IN_PROGRESS_STATUSES = new Set<ScanStatus>([
  "queued",
  "snapshotting",
  "detecting",
  "validating",
  "indexing",
]);

export function scanDiagramOutcome(status: ScanStatus): ScanDiagramOutcome {
  if (IN_PROGRESS_STATUSES.has(status)) return "in-progress";
  if (status === "completed") return "complete";
  if (status === "completed_with_errors") return "warning";
  if (status === "failed") return "failed";
  return "canceled";
}

export function scanDiagramToneLabel(tone: ScanDiagramNodeTone): string {
  if (tone === "in-progress") return "In progress";
  if (tone === "complete") return "Complete";
  if (tone === "warning") return "Warning";
  if (tone === "failed") return "Failed";
  if (tone === "canceled") return "Canceled";
  if (tone === "locked") return "Locked";
  return "Pending";
}

function identityText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/** Authorized source identity is the repository name plus repository id. */
export function hasAuthorizedSourceIdentity(scan: ScanView): boolean {
  return Boolean(identityText(scan.repositoryName) && identityText(scan.repositoryId));
}

/** Snapshot/revision identity uses hashes already present on ScanView. */
export function hasSnapshotIdentity(scan: ScanView): boolean {
  return Boolean(identityText(scan.snapshotId) || identityText(scan.snapshotFingerprint));
}

function activeNodeIndex(status: ScanStatus): number {
  switch (status) {
    case "queued":
    case "snapshotting":
      return 1;
    case "detecting":
      return 2;
    case "validating":
      return 3;
    case "indexing":
      return 4;
    default:
      return 5;
  }
}

function nodeHasEvidence(id: ScanDiagramNodeId, scan: ScanView): boolean {
  const { status } = scan;
  switch (id) {
    case "authorized-source":
      return hasAuthorizedSourceIdentity(scan);
    case "immutable-snapshot":
      return hasSnapshotIdentity(scan);
    case "detectors":
      return (
        status === "validating" ||
        status === "indexing" ||
        status === "completed" ||
        status === "completed_with_errors"
      );
    case "independent-replay":
      return status === "indexing" || status === "completed" || status === "completed_with_errors";
    case "index":
      return status === "completed" || status === "completed_with_errors";
    case "findings":
      return status === "completed" || status === "completed_with_errors";
  }
}

function nodeTone(
  id: ScanDiagramNodeId,
  index: number,
  scan: ScanView,
  gateLocked: boolean,
): ScanDiagramNodeTone {
  if (gateLocked) return "locked";
  const { status } = scan;
  const evidence = nodeHasEvidence(id, scan);
  if (evidence) {
    if (id === "findings" && status === "completed_with_errors") return "warning";
    return "complete";
  }
  if (status === "failed" && id === "findings") return "failed";
  if (status === "cancelled" && id === "findings") return "canceled";
  if (status === "queued" && id === "immutable-snapshot") return "pending";
  if (index === activeNodeIndex(status) && IN_PROGRESS_STATUSES.has(status)) return "in-progress";
  return "pending";
}

export function deriveScanDiagram(scan: ScanView): ScanDiagram {
  const gateLocked = !hasAuthorizedSourceIdentity(scan);
  const outcome = scanDiagramOutcome(scan.status);
  const nodes = SCAN_DIAGRAM_NODE_IDS.map((id, index) => {
    const tone = nodeTone(id, index, scan, gateLocked);
    return {
      id,
      label: NODE_LABELS[id],
      tone,
      locked: tone === "locked",
    };
  });
  return { outcome, gateLocked, nodes };
}
